import { edgeColumns } from './edgeColumns.ts';
import { loadCoachInfluenceContext, type SupabaseLikeClient } from "./coachInfluenceContext.ts";
import { fetchFrameworkPackById, type DbFrameworkPack } from "./frameworkPackDb.ts";
import { resolveLeadershipLensPackId } from "./leadershipLens.ts";

interface ResolveLeaderPackOpts {
  coachId?: string | null;
  coachLensJson?: { framework_pack_id?: string | null } | null;
  leadershipLens?: string | null;
  throwOnError?: boolean;
}

/**
 * Unified resolver for a leader's philosophy framework pack.
 *
 * Priority:
 *   1. If leader has a coach with framework_pack_id set → use that pack
 *   2. If solo leader (or coach with no pack_id) → map leadership_lens via LENS_TO_PACK_ID
 *   3. Returns null → Veera uses default behavior
 */
export async function resolveLeaderFrameworkPack(
  supabase: SupabaseLikeClient,
  opts: ResolveLeaderPackOpts,
  includeQuestionBank = false,
): Promise<DbFrameworkPack | null> {
  // Path 1: coach-assigned leader — use coach's declared pack
  if (opts.coachId && opts.coachLensJson?.framework_pack_id) {
    const pack = await fetchFrameworkPackById(
      supabase,
      opts.coachLensJson.framework_pack_id,
      includeQuestionBank,
      { throwOnError: opts.throwOnError },
    ).catch((err) => {
      if (opts.throwOnError) throw new Error("framework_pack_query_failed");
      console.warn("[resolveLeaderFrameworkPack] Failed to fetch coach pack", err);
      return null;
    });
    // Strict callers need the selected family authority; missing is unavailable too.
    if (!pack && opts.throwOnError) throw new Error("framework_pack_unavailable");
    // An explicit current selection never falls back to another philosophy after failure/missing data.
    return pack;
  }

  // Path 2: solo leader or coach without pack — map leader's own lens
  const packId = resolveLeadershipLensPackId(opts.leadershipLens);
  if (!packId) return null;

  const pack = await fetchFrameworkPackById(supabase, packId, includeQuestionBank, { throwOnError: opts.throwOnError }).catch((err) => {
    if (opts.throwOnError) throw new Error("framework_pack_query_failed");
    console.warn("[resolveLeaderFrameworkPack] Failed to fetch lens pack", err);
    return null;
  });
  if (!pack && opts.throwOnError) throw new Error("framework_pack_unavailable");
  return pack;
}

/** Existing surface selection policies are explicit; every call reads current
 * authority rather than reusing the values captured before inference. */
export interface CurrentFrameworkPackScope {
  leaderId?: string | null;
  coachId?: string | null;
  coachSource: 'actor' | 'profile' | 'link';
  /** Existing leader fast path intentionally selects only the leader lens. */
  leaderLensOnly?: boolean;
}
interface SelectionQuery {
  select(columns:string):SelectionQuery;
  eq(column:string,value:unknown):SelectionQuery;
  maybeSingle():PromiseLike<{data:Record<string,unknown>|null;error:unknown}>;
}
export async function resolveCurrentFrameworkPack(
  client:SupabaseLikeClient, scope:CurrentFrameworkPackScope, includeQuestionBank:boolean,
):Promise<DbFrameworkPack|null>{
  let coachId=scope.coachId??null;
  if(scope.coachSource!=='actor'){
    if(!scope.leaderId)throw new Error('framework_authority_unavailable');
    const query=scope.coachSource==='profile'
      ?(client.from('profiles') as SelectionQuery).select(edgeColumns('profiles',['coach_id'])).eq('id',scope.leaderId)
      :(client.from('coach_leader_links') as SelectionQuery).select(edgeColumns('coach_leader_links',['coach_id'])).eq('leader_id',scope.leaderId);
    const {data,error}=await query.maybeSingle();
    if(error)throw new Error('framework_authority_unavailable');
    if(scope.coachSource==='profile'&&!data)throw new Error('framework_authority_unavailable');
    coachId=typeof data?.coach_id==='string'?data.coach_id:null;
  }
  const influence=coachId&&!scope.leaderLensOnly?await loadCoachInfluenceContext(client,coachId,{throwOnError:true}):null;
  let leadershipLens:string|null=null;
  if(scope.leaderId){
    const row=await (client.from('leader_onboarding') as SelectionQuery).select(edgeColumns('leader_onboarding',['leadership_lens'])).eq('leader_id',scope.leaderId).maybeSingle();
    if(row.error)throw new Error('framework_authority_unavailable');
    leadershipLens=typeof row.data?.leadership_lens==='string'?row.data.leadership_lens:null;
  }
  return resolveLeaderFrameworkPack(client,{coachId,coachLensJson:influence?.coachLens?.coach_lens_json,leadershipLens,throwOnError:true},includeQuestionBank);
}
